import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useTenant } from "@/contexts/TenantContext";
import { useOptimizedPermissions } from "@/hooks/useOptimizedPermissions";
import {
  Mail,
  CheckCircle,
  AlertCircle,
  Loader2,
  Info,
  ExternalLink,
  Copy,
  Check,
  ChevronDown,
} from "lucide-react";

const DEFAULT_DOMAIN = "mail.joulescrm.com";

export function EmailDomainConfiguration() {
  const [emailDomain, setEmailDomain] = useState<string>("");
  const [emailEnabled, setEmailEnabled] = useState<boolean>(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [copiedRecord, setCopiedRecord] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const { currentTenant } = useTenant();
  const { isSuperadmin } = useOptimizedPermissions();

  useEffect(() => {
    loadConfig();
  }, [currentTenant?.tenant_id]);

  const loadConfig = async () => {
    if (!currentTenant?.tenant_id) {
      return;
    }

    try {
      const { data, error } = await supabase
        .from("tenants")
        .select("email_domain, email_enabled")
        .eq("tenant_id", currentTenant.tenant_id)
        .single();

      if (error) throw error;

      setEmailDomain(data?.email_domain || "");
      setEmailEnabled(data?.email_enabled !== false); // Default to true
    } catch (error) {
      console.error("Error loading email config:", error);
      toast.error("Failed to load email configuration");
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("tenants")
        .update({
          email_domain: emailDomain || null,
          email_enabled: emailEnabled,
        })
        .eq("tenant_id", currentTenant?.tenant_id);

      if (error) throw error;

      toast.success("Email configuration saved successfully");
    } catch (error) {
      console.error("Error saving config:", error);
      toast.error("Failed to save email configuration");
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text: string, recordType: string) => {
    navigator.clipboard.writeText(text);
    setCopiedRecord(recordType);
    toast.success(`${recordType} copied to clipboard`);
    setTimeout(() => setCopiedRecord(null), 2000);
  };

  const currentDomain = emailDomain || DEFAULT_DOMAIN;
  const isCustomDomain = !!emailDomain;

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Email Domain Configuration</CardTitle>
                <CardDescription>
                  Configure the email domain for sending notifications and system emails
                </CardDescription>
              </div>
              <ChevronDown
                className={cn(
                  'h-5 w-5 text-muted-foreground transition-transform duration-200',
                  isExpanded && 'rotate-180'
                )}
              />
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-6 pt-0">
        {/* Current Email Domain Display with Enable Toggle */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Current Email Domain</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Email Notifications</span>
              <Switch
                id="email-enabled"
                checked={emailEnabled}
                onCheckedChange={setEmailEnabled}
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 p-3 bg-muted rounded-lg font-mono text-sm">
              notifications@{currentDomain}
            </div>
            {!isCustomDomain && (
              <span className="text-xs text-muted-foreground px-2 py-1 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded">
                Default
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            All system emails will be sent from this address
          </p>
        </div>

        {/* Custom Domain Input */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor="custom-domain">Custom Email Domain (Optional)</Label>
            {isSuperadmin && (
              <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-auto py-1">
                    <Info className="h-3 w-3 mr-1" />
                    <span className="text-xs">Setup Instructions</span>
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Custom Domain Setup Instructions</DialogTitle>
                  <DialogDescription>
                    Follow these steps to configure your custom email domain in Postmark
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 text-sm">
                  {/* Step 1 */}
                  <div className="space-y-2">
                    <h4 className="font-semibold flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs">
                        1
                      </span>
                      Add Domain to Postmark
                    </h4>
                    <p className="text-muted-foreground ml-8">
                      Log into the Postmark dashboard and add your domain under "Sender Signatures" → "Domains"
                    </p>
                  </div>

                  {/* Step 2 */}
                  <div className="space-y-3">
                    <h4 className="font-semibold flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs">
                        2
                      </span>
                      Add DNS Records
                    </h4>
                    <p className="text-muted-foreground ml-8">
                      Add these DNS records to your domain (Postmark will provide exact values):
                    </p>

                    {/* DKIM Record */}
                    <div className="ml-8 p-3 bg-muted rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">DKIM Record (TXT)</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard("DKIM", "DKIM Record Type")}
                        >
                          {copiedRecord === "DKIM Record Type" ? (
                            <Check className="h-3 w-3" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                      <code className="text-xs block">
                        Type: TXT<br />
                        Hostname: [provided by Postmark]<br />
                        Value: k=rsa; p=...
                      </code>
                    </div>

                    {/* Return-Path Record */}
                    <div className="ml-8 p-3 bg-muted rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Return-Path (CNAME)</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard("CNAME", "Return-Path Record Type")}
                        >
                          {copiedRecord === "Return-Path Record Type" ? (
                            <Check className="h-3 w-3" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                      <code className="text-xs block">
                        Type: CNAME<br />
                        Hostname: pm-bounces.yourdomain.com<br />
                        Value: pm.mtasv.net
                      </code>
                    </div>

                    {/* SPF Record */}
                    <div className="ml-8 p-3 bg-muted rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">SPF Record (TXT) - Optional but Recommended</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard("v=spf1 include:spf.mtasv.net ~all", "SPF Record")}
                        >
                          {copiedRecord === "SPF Record" ? (
                            <Check className="h-3 w-3" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                      <code className="text-xs block">
                        Type: TXT<br />
                        Hostname: yourdomain.com<br />
                        Value: v=spf1 include:spf.mtasv.net ~all
                      </code>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="space-y-2">
                    <h4 className="font-semibold flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs">
                        3
                      </span>
                      Verify Domain in Postmark
                    </h4>
                    <p className="text-muted-foreground ml-8">
                      Wait 15-30 minutes for DNS propagation, then click "Verify" in Postmark. All records should show green checkmarks.
                    </p>
                  </div>

                  {/* Step 4 */}
                  <div className="space-y-2">
                    <h4 className="font-semibold flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs">
                        4
                      </span>
                      Update Domain Here
                    </h4>
                    <p className="text-muted-foreground ml-8">
                      Once verified in Postmark, enter your domain above and click "Save Configuration"
                    </p>
                  </div>

                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      <strong>Need help?</strong> See the complete setup guide at{" "}
                      <code className="text-xs bg-muted px-1 rounded">/docs/TENANT-CUSTOM-DOMAIN-SETUP-GUIDE.md</code>
                    </AlertDescription>
                  </Alert>

                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" asChild className="flex-1">
                      <a
                        href="https://account.postmarkapp.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-3 w-3 mr-2" />
                        Open Postmark Dashboard
                      </a>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowInstructions(false)}
                      className="flex-1"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            )}
          </div>

          <Input
            id="custom-domain"
            type="text"
            value={emailDomain}
            onChange={(e) => setEmailDomain(e.target.value)}
            placeholder="yourdomain.com"
          />
          <p className="text-xs text-muted-foreground">
            Leave empty to use the default domain ({DEFAULT_DOMAIN}). Custom domains must be verified in Postmark first.
          </p>
        </div>

        {/* Warning if custom domain is set */}
        {emailDomain && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Important:</strong> Make sure this domain is verified in Postmark and DNS records are configured correctly, or emails will fail to send.
            </AlertDescription>
          </Alert>
        )}

        {/* Save Button */}
        <div className="flex gap-2">
          <Button onClick={saveConfig} disabled={saving} className="flex-1">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <CheckCircle className="mr-2 h-4 w-4" />
            Save Configuration
          </Button>
        </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
